import {
  NUTRIENT_KEYS,
  type IngredientRef,
  type NutriScoreGrade,
  type Product,
  type ProductUpdateInput,
} from '@recipes/contracts';
import { normalizeUnit } from '@recipes/domain';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@recipes/ui';
import { Camera } from 'lucide-react';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toastError } from '@/lib/errors';
import { captureProduct, patchProduct } from './catalog-queue';
import { LabelScan, type LabelValues } from './label-scan';
import { IngredientCombobox } from './ingredient-combobox';

const NO_SCORE = '__unknown__';
const NONE_PRINTED = '__none__';
type ScoreChoice = NutriScoreGrade | typeof NO_SCORE | typeof NONE_PRINTED;

const decimal = (raw: string): string | null => {
  const value = raw.trim().replace(',', '.');
  return /^\d+(\.\d+)?$/.test(value) ? value : null;
};

/**
 * Capture a product (unknown barcode) or correct one (docs/finished/ingredient-catalog-spec.md 5.2).
 * Saving works with a name alone and without a connection – everything goes through the
 * catalog queue; the label scan is the only part that needs to be online.
 */
export function ProductForm({
  product,
  barcode,
  onSaved,
  onCancel,
}: {
  product?: Product | null;
  barcode?: string | null;
  onSaved: (product: Product) => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const ids = {
    name: useId(),
    brand: useId(),
    ingredient: useId(),
    quantity: useId(),
    unit: useId(),
    score: useId(),
    text: useId(),
  };
  const photoRef = useRef<HTMLInputElement | null>(null);
  const [name, setName] = useState(product?.name ?? '');
  const [brand, setBrand] = useState(product?.brand ?? '');
  const [ingredient, setIngredient] = useState<Pick<IngredientRef, 'id' | 'name'> | null>(
    product?.ingredient ?? null,
  );
  const [quantity, setQuantity] = useState(product?.packageQuantity ?? '');
  const [unit, setUnit] = useState(product?.packageUnitCode ?? product?.packageUnitText ?? '');
  const [score, setScore] = useState<ScoreChoice>(
    product?.nutriScore ?? (product?.nutriScoreNone ? NONE_PRINTED : NO_SCORE),
  );
  const [ingredientsText, setIngredientsText] = useState(product?.ingredientsText ?? '');
  const [nutrition, setNutrition] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      NUTRIENT_KEYS.map((key) => [key, product?.nutrition?.[key] ?? '']).filter(
        ([, v]) => v !== '',
      ),
    ),
  );
  const [label, setLabel] = useState<LabelValues | null>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const applyLabel = (values: LabelValues) => {
    setLabel(values);
    if (values.ingredientsText) setIngredientsText(values.ingredientsText);
    if (values.nutrition) {
      const {
        basis: _basis,
        portionQuantity: _pq,
        portionUnitText: _pu,
        ...rest
      } = values.nutrition;
      setNutrition(
        Object.fromEntries(
          Object.entries(rest).filter(
            (entry): entry is [string, string] => typeof entry[1] === 'string',
          ),
        ),
      );
    }
    if (values.nutriScore) setScore(values.nutriScore);
    if (values.packageQuantity && !quantity) setQuantity(values.packageQuantity);
    if (values.packageUnitText && !unit) setUnit(values.packageUnitText);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const unitCode = unit.trim() ? (normalizeUnit(unit.trim())?.code ?? null) : null;
      const values = Object.fromEntries(
        Object.entries(nutrition)
          .map(([key, raw]) => [key, decimal(raw)] as const)
          .filter(([, v]) => v !== null),
      );
      const fields: Omit<ProductUpdateInput, 'version'> = {
        name: name.trim(),
        brand: brand.trim() || null,
        ingredientId: ingredient?.id ?? null,
        packageQuantity: decimal(quantity),
        packageUnitCode: unitCode,
        packageUnitText: unitCode ? null : unit.trim() || null,
        ingredientsText: ingredientsText.trim() || null,
        ...(label?.allergens ? { allergens: label.allergens } : {}),
        ...(label?.additives ? { additives: label.additives } : {}),
        ...(Object.keys(values).length
          ? {
              nutrition: {
                basis: label?.nutrition?.basis ?? product?.nutrition?.basis ?? 'PER_100_G',
                ...values,
              },
            }
          : {}),
        ...(score === NO_SCORE
          ? {}
          : score === NONE_PRINTED
            ? { nutriScoreNone: true }
            : { nutriScore: score }),
        ...(label ? { dataSource: 'LABEL_SCAN' as const } : {}),
      };
      const saved = product
        ? await patchProduct(product, fields)
        : await captureProduct({
            body: { ...fields, name: name.trim(), barcode: barcode ?? null },
            photos: photo ? [{ file: photo, name: photo.name, role: 'FRONT' }] : [],
          });
      onSaved(saved);
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      noValidate
      className="space-y-4"
      data-testid="product-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) void submit();
      }}
    >
      {barcode && (
        <p className="text-sm text-muted-foreground">
          {t('catalog.product.barcode')}: <span className="font-mono">{barcode}</span>
        </p>
      )}
      <div>
        <Label htmlFor={ids.name}>{t('catalog.product.name')} *</Label>
        <Input
          id={ids.name}
          className="mt-1"
          required
          maxLength={200}
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="product-name"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor={ids.brand}>{t('catalog.product.brand')}</Label>
          <Input
            id={ids.brand}
            className="mt-1"
            maxLength={120}
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor={ids.ingredient}>{t('catalog.product.ingredientField')}</Label>
          {/* Suggestions from the device copy; a missing ingredient can be created on the spot. */}
          <IngredientCombobox
            id={ids.ingredient}
            initial={product?.ingredient ?? null}
            onChange={setIngredient}
            placeholder={t('catalog.product.ingredientPlaceholder')}
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label htmlFor={ids.quantity}>{t('catalog.product.packageQuantity')}</Label>
          <Input
            id={ids.quantity}
            className="mt-1"
            inputMode="decimal"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor={ids.unit}>{t('catalog.product.packageUnit')}</Label>
          <Input
            id={ids.unit}
            className="mt-1"
            maxLength={40}
            value={unit}
            placeholder="g"
            onChange={(e) => setUnit(e.target.value)}
          />
        </div>
        <div>
          <Label htmlFor={ids.score}>Nutri-Score</Label>
          <Select value={score} onValueChange={(v) => setScore(v as ScoreChoice)}>
            <SelectTrigger id={ids.score} className="mt-1" data-testid="product-nutri-score">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_SCORE}>{t('catalog.nutriScore.unknown')}</SelectItem>
              {(['A', 'B', 'C', 'D', 'E'] as const).map((grade) => (
                <SelectItem key={grade} value={grade}>
                  {grade}
                </SelectItem>
              ))}
              <SelectItem value={NONE_PRINTED}>{t('catalog.nutriScore.none')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {!product && (
        <div>
          <input
            ref={photoRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            tabIndex={-1}
            aria-hidden
            onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
            data-testid="product-photo-input"
          />
          <Button type="button" variant="outline" onClick={() => photoRef.current?.click()}>
            <Camera aria-hidden />{' '}
            {photo ? t('catalog.product.photoChange') : t('catalog.product.photoAdd')}
          </Button>
          {photo && <span className="ml-2 text-xs text-muted-foreground">{photo.name}</span>}
        </div>
      )}

      <LabelScan
        productId={product?.scope === 'GLOBAL' ? null : (product?.id ?? null)}
        onApply={applyLabel}
      />

      <details className="rounded-md border p-2" open={Boolean(label)}>
        <summary className="cursor-pointer text-sm font-medium">
          {t('catalog.product.details')}
        </summary>
        <div className="mt-3 space-y-3">
          <div>
            <Label htmlFor={ids.text}>{t('catalog.product.ingredientsText')}</Label>
            <Textarea
              id={ids.text}
              className="mt-1"
              rows={3}
              maxLength={4000}
              value={ingredientsText}
              onChange={(e) => setIngredientsText(e.target.value)}
            />
          </div>
          <fieldset>
            <legend className="mb-1 text-sm font-medium">{t('catalog.nutrition.PER_100_G')}</legend>
            <div className="grid grid-cols-3 gap-2">
              {NUTRIENT_KEYS.map((key) => (
                <div key={key}>
                  <Label htmlFor={`${ids.text}-${key}`} className="text-xs">
                    {t(`catalog.nutrient.${key}`)}
                  </Label>
                  <Input
                    id={`${ids.text}-${key}`}
                    className="mt-0.5"
                    inputMode="decimal"
                    value={nutrition[key] ?? ''}
                    onChange={(e) => setNutrition((prev) => ({ ...prev, [key]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
          </fieldset>
        </div>
      </details>

      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        )}
        <Button type="submit" loading={saving} disabled={!name.trim()} data-testid="product-save">
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
}
